/**
 * Compact summary grid at the top of the ASN detail modal: parties, courier,
 * tracking, dates, weight and emissions. Extracted from ASNDetailModal to keep
 * that file under the 500-line limit.
 */
import { Sparkles } from 'lucide-react';
import { ASNData } from './asnModalTypes';
import ASNTrackingNumberField from './tracking/ASNTrackingNumberField';
import { CourierStatusBadge, isTrackableASN } from './tracking/CourierStatusBadge';
import { formatCourier } from './asnCourier';

interface ASNSummaryGridProps {
  /** The ASN being viewed. */
  asn: ASNData;
  /** Column label for the receiving company. */
  counterpartyLabel: string;
  /** Column label for the sending site. */
  siteLabel: string;
  /** Date formatter from the user's preferences. */
  formatDate: (dateString?: string) => string;
  /** Called after an inline edit so the modal can refetch. */
  onEdited: () => void;
}

/**
 * Renders the ASN summary grid.
 *
 * @param asn - The ASN being viewed.
 * @param counterpartyLabel - Label for the receiving company.
 * @param siteLabel - Label for the sending site.
 * @param formatDate - Date formatter from the user's preferences.
 * @param onEdited - Fired after the tracking number is edited inline.
 * @returns The summary grid.
 */
export default function ASNSummaryGrid({ asn: asnData, counterpartyLabel, siteLabel, formatDate, onEdited }: ASNSummaryGridProps) {
  return (
    <div className="px-6 py-4 border-b bg-muted/20">
      <div className="grid grid-cols-4 gap-x-8 gap-y-3 text-sm">
        <div><span className="text-muted-foreground">ASN Number</span><p className="font-medium">{asnData.asn_number}</p></div>
        <div><span className="text-muted-foreground">{counterpartyLabel}</span><p className="font-medium">{asnData.customer_details?.name || '-'}</p></div>
        <div><span className="text-muted-foreground">{siteLabel}</span><p className="font-medium">{asnData.supplier_details?.name || '-'}</p></div>
        <div><span className="text-muted-foreground">Courier</span><p className="font-medium">{formatCourier(asnData.courier, asnData.courier_other).toUpperCase()}</p></div>
        <ASNTrackingNumberField asn={asnData} onUpdated={onEdited} />
        <div><span className="text-muted-foreground">Transport</span><p className="font-medium capitalize">{asnData.method_of_transport || '-'}</p></div>
        <div>
          <span className="text-muted-foreground">Expected Delivery</span>
          {/* Fall back to the courier's own estimate so the field isn't
              blank whenever the ASN never carried a date of its own. */}
          {asnData.estimated_delivery_date ? (
            <p className="font-medium">{formatDate(asnData.estimated_delivery_date)}</p>
          ) : asnData.tracking_summary?.estimated_delivery_date ? (
            <p className="font-medium flex items-center gap-1" title="Live estimate from the courier">
              <Sparkles className="h-3 w-3 text-amber-500" />
              {formatDate(asnData.tracking_summary.estimated_delivery_date)}
            </p>
          ) : (
            <p className="font-medium">-</p>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Courier Status</span>
          <p className="font-medium">
            <CourierStatusBadge
              tracking={asnData.tracking_summary}
              proEnabled={asnData.tracking_pro_enabled}
              trackable={isTrackableASN(asnData.courier, asnData.tracking_number, asnData.courier_other)}
            />
          </p>
        </div>
        <div><span className="text-muted-foreground">Actual Delivery</span><p className="font-medium">{formatDate(asnData.actual_delivery_date)}</p></div>
        <div><span className="text-muted-foreground">Weight (kg)</span><p className="font-medium">{asnData.weight_kg || '-'}</p></div>
        <div><span className="text-muted-foreground">Distance (km)</span><p className="font-medium">{asnData.distance_km || '-'}</p></div>
        <div><span className="text-muted-foreground">Carbon (kg CO2)</span><p className="font-medium text-green-700">{asnData.carbon_emission_kg || asnData.calculated_carbon_emission || '-'}</p></div>
        {asnData.notes && <div><span className="text-muted-foreground">Notes</span><p className="font-medium truncate" title={asnData.notes}>{asnData.notes}</p></div>}
      </div>
    </div>
  );
}
